import { useCallback, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Trans, useLingui } from '@lingui/react/macro'
import { Mail01Icon } from '@hugeicons/core-solid-rounded'

import * as Form from '@/features/form'
import Input from '@/components/ui/Input'
import { dialogContainer, DialogHeader } from '@/layouts/Dialog'
import { useAuth } from '../hooks/useAuth'

type FormData = {
  email: string
}

function ForgotPassswordContent() {
  const { t } = useLingui()

  const { resetPassword } = useAuth()
  const [showSuccess, setShowSuccess] = useState(false)

  const reset = useCallback(
    async (data: FormData) => {
      await resetPassword(data.email)

      setShowSuccess(true)
    },
    [resetPassword, setShowSuccess]
  )

  return (
    <Form.Root
      onSubmit={reset}
      defaultValues={{
        email: ''
      }}
    >
      <div className="flex flex-col gap-6 pt-2.5 px-2.5">
        <Form.Field
          name="email"
          label={t`Email`}
          rules={{
            validate: {
              required: Form.Validations.requiredWithTrimValidation(
                t`This field is required`
              )
            }
          }}
        >
          {({ renderProps: { onChange, ...props } }) => (
            <Input
              type="text"
              Icon={Mail01Icon}
              onChange={event => onChange(event.target.value)}
              placeholder=""
              autoComplete="given-name webauthn"
              {...props}
            />
          )}
        </Form.Field>
        {!showSuccess && (
          <div className="flex flex-row justify-end">
            <Form.Submit>
              <Trans>Submit</Trans>
            </Form.Submit>
          </div>
        )}
        {showSuccess && (
          <span className="text-text text-regular">
            <Trans>
              You should get an email with a password reset link into your
              inbox, if the given email exists
            </Trans>
          </span>
        )}
      </div>
    </Form.Root>
  )
}

export function ForgotPassword() {
  const { t } = useLingui()
  const [open, setOpen] = useState(false)

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger className="rounded-xsmall">
        <span className="text-info-500 underline cursor-pointer">
          <Trans>Forgot password</Trans>
        </span>
      </Dialog.Trigger>
      <Dialog.Portal container={dialogContainer}>
        <Dialog.Overlay />
        <Dialog.Content className="absolute left-1/2 top-1/2 mx-auto w-screen max-w-[450px] -translate-x-1/2 -translate-y-1/2 rounded bg-white p-8 shadow-md">
          <DialogHeader
            title={t`Reset password`}
            description={t({
              comment:
                'Description of a dialog that takes an email to send a reset link, so the user can set a new password.',
              message:
                'Resetting of a password for a specified email, where you forgot your password to.'
            })}
            close={() => setOpen(false)}
          />
          <ForgotPassswordContent />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
